import { Button } from "@aurumiq/ui/components/button";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { dmy, relativeTime } from "@aurumiq/ui/lib/format";
import { Ban, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { ApiKeyDialog } from "@/features/system/api-key-dialog";
import { useApiKeys, useRevokeApiKey } from "@/features/system/queries";
import { errorMessage } from "@/lib/api";
import type { ApiKey } from "@/lib/types";

const col = createColumnHelper<ApiKey>();

export function ApiKeysPage() {
  const canWrite = usePermission("apikeys:write");
  const keys = useApiKeys();
  const revoke = useRevokeApiKey();
  const [createOpen, setCreateOpen] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Name",
        cell: (c) => <span className="font-semibold">{c.getValue()}</span>,
      }),
      col.accessor("scopes", {
        header: "Scopes",
        cell: (c) => (
          <span className="flex flex-wrap gap-1">
            {c.getValue().map((s) => (
              <StatusTag key={s} className="font-mono font-normal">
                {s}
              </StatusTag>
            ))}
          </span>
        ),
      }),
      col.accessor("created_by", { header: "Created by", cell: (c) => c.getValue() ?? "—" }),
      col.accessor("created_at", { header: "Created", cell: (c) => dmy(c.getValue()) }),
      col.accessor("last_used_at", {
        header: "Last used",
        cell: (c) => relativeTime(c.getValue()),
      }),
      col.accessor("revoked_at", {
        header: "Status",
        cell: (c) =>
          c.getValue() ? (
            <StatusTag variant="err">REVOKED {dmy(c.getValue())}</StatusTag>
          ) : (
            <StatusTag variant="ok">ACTIVE</StatusTag>
          ),
      }),
      col.display({
        id: "actions",
        header: "",
        meta: { numeric: true },
        cell: (c) =>
          canWrite && !c.row.original.revoked_at ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              onClick={() => setRevoking(c.row.original)}
            >
              <Ban /> Revoke
            </Button>
          ) : null,
      }),
    ],
    [canWrite],
  );

  return (
    <>
      <PageHeader
        title="API keys"
        description="Machine credentials for CI and integrations. Keys are shown once at creation."
        actions={
          canWrite ? (
            <Button variant="gold" onClick={() => setCreateOpen(true)}>
              <Plus /> Create key
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns as ColumnDef<ApiKey, unknown>[]}
        data={keys.data?.items ?? []}
        isLoading={keys.isPending}
        getRowId={(k) => k.id}
        emptyMessage={
          keys.isError ? `Could not load API keys: ${errorMessage(keys.error)}` : "No API keys yet."
        }
      />
      <ApiKeyDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ReasonDialog
        open={revoking !== null}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Revoke "${revoking?.name}"?`}
        description="Anything using this key stops working immediately. This cannot be undone."
        confirmLabel="Revoke key"
        onConfirm={async (reason) => {
          if (revoking) await revoke.mutateAsync({ id: revoking.id, reason });
        }}
      />
    </>
  );
}
